import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router';
import { ChangeEvent, SubmitEvent, useCallback, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import type { Contact } from '../../models/Contact';

export interface ContactFormState {
  values: Partial<Omit<Contact, 'id'>>;
  errors: Partial<Record<keyof ContactFormState['values'], string>>;
}

export interface ContactFormProps {
  formState: ContactFormState;
  onFieldChange: (
    name: keyof ContactFormState['values'],
    value: FormFieldValue,
  ) => void;
  onSubmit: (formValues: Partial<ContactFormState['values']>) => Promise<void>;
  onReset?: (formValues: Partial<ContactFormState['values']>) => void;
  submitButtonLabel: string;
  backButtonPath?: string;
}

export default function ContactForm(props: ContactFormProps) {
  const {
    formState,
    onFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath,
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault();

      setIsSubmitting(true);
      try {
        await onSubmit(formValues);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, onSubmit],
  );

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof ContactFormState['values'],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(formValues);
    }
  }, [formValues, onReset]);

  const handleBack = useCallback(() => {
    navigate(backButtonPath ?? '/dashboard/contacts');
  }, [navigate, backButtonPath]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 10, sm: 4 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.name ?? ''}
              onChange={handleTextFieldChange}
              name="name"
              label="Name"
              autoComplete="name"
              error={!!formErrors.name}
              helperText={formErrors.name ?? ' '}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }} sx={{ display: 'flex' }}>
            <TextField
              type="email"
              value={formValues.email ?? ''}
              onChange={handleTextFieldChange}
              name="email"
              label="Email"
              autoComplete="email"
              error={!!formErrors.email}
              helperText={formErrors.email ?? ' '}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }} sx={{ display: 'flex' }}>
            <TextField
              type="tel"
              value={formValues.phone ?? ''}
              onChange={handleTextFieldChange}
              name="phone"
              label="Phone"
              autoComplete="tel"
              error={!!formErrors.phone}
              helperText={formErrors.phone ?? ' '}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              fullWidth
            />
          </Grid>
        </Grid>
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          color="success"
          startIcon={<SaveIcon />}
          loading={isSubmitting}
        >
          {submitButtonLabel}
        </Button>
      </Stack>
    </Box>
  );
}
